import React from "react"
import {regionTextByCode} from "@casas-diamante/types"
import {getImageURL} from "@/utils"
import {PropertyResponse as Property} from "@/types/Property"
import {
   PROPERTY_TABLE_PAGE_SIZE,
   propertySortLabels,
   useCondoPropertiesTable,
} from "@/hooks/useCondoPropertiesTable"
import {CondoPropertiesSortBy} from "@/types/Condo"
import StatusTile from "@/components/properties/StatusTile"
import StatusIndicator from "@/components/properties/StatusIndicator"

const formatCurrency = (value: unknown) => {
   const numberValue = Number(value)
   if (!Number.isFinite(numberValue) || numberValue <= 0) return "-"
   return `$${numberValue.toLocaleString("en-US")} MXN`
}

const formatNumber = (value: unknown, suffix = "") => {
   const numberValue = Number(value)
   if (!Number.isFinite(numberValue) || numberValue <= 0) return "-"
   return `${numberValue.toLocaleString("en-US")}${suffix}`
}

const formatCount = (value: number) => value.toLocaleString("en-US")

const getSurface = (property: Property) => property.mtsConstruction || property.mts || null

function SortSelect({
   value,
   onChange,
}: {
   value: CondoPropertiesSortBy
   onChange: (value: CondoPropertiesSortBy) => void
}) {
   return (
      <label className="flex items-center gap-2 text-sm font-bold text-blue">
         Ordenar por
         <select
            value={value}
            onChange={(event) => onChange(event.target.value as CondoPropertiesSortBy)}
            className="rounded-md border border-blue bg-white px-3 py-2 text-blue outline-none"
         >
            {Object.entries(propertySortLabels).map(([optionValue, label]) => (
               <option key={optionValue} value={optionValue}>
                  {label}
               </option>
            ))}
         </select>
      </label>
   )
}

function PropertyRow({
   property,
   index,
   onOpen,
}: {
   property: Property
   index: number
   onOpen: (property: Property) => void
}) {
   const openProperty = () => onOpen(property)
   const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return
      event.preventDefault()
      openProperty()
   }

   return (
      <tr
         data-property-id={property.id}
         tabIndex={0}
         className={`cursor-pointer transition-colors hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
            index % 2 === 0 ? "bg-white" : "bg-gray-50"
         }`}
         onClick={openProperty}
         onKeyDown={handleKeyDown}
      >
         <td className="border border-gray-200 px-3 py-3">
            <div className="flex min-w-[260px] items-center gap-3">
               <img
                  src={getImageURL(property.mainImage)}
                  alt={property.name}
                  className="h-14 w-20 flex-none rounded object-cover"
               />
               <div className="min-w-0">
                  <p className="break-words whitespace-normal font-bold text-gray-800">
                     {property.name}
                  </p>
                  <p className="truncate text-xs text-gray-600">
                     {regionTextByCode[property.region]}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                     {property.forSale && (
                        <span className="rounded border border-blue px-2 py-0.5 text-xs text-blue">
                           Venta
                        </span>
                     )}
                     {property.forRent && (
                        <span className="rounded border border-green px-2 py-0.5 text-xs text-green">
                           Renta
                        </span>
                     )}
                  </div>
               </div>
            </div>
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">{property.type || "-"}</td>
         <td className="border border-gray-200 px-3 py-3 text-center font-bold">
            {formatCurrency(property.costoVenta)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center font-bold">
            {formatCurrency(property.costos?.noche_regular)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center font-bold">
            {formatCurrency(property.costos?.mensual_anual)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            {formatCurrency(property.maintenance)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            {formatNumber(getSurface(property), " m²")}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            {formatNumber(property.rooms)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            {formatNumber(property.bathrooms)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            {formatNumber(property.parkingLots)}
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            <StatusTile status={property.status} />
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            <StatusIndicator
               label={
                  property.suspended
                     ? "Apagado"
                     : property.suspended === undefined
                     ? "Sin actualizar"
                     : "Encendido"
               }
               condition={property.suspended === undefined ? undefined : !property.suspended}
            />
         </td>
         <td className="border border-gray-200 px-3 py-3 text-center">
            <StatusIndicator
               label={
                  property.available
                     ? "Disponible"
                     : property.available === undefined
                     ? "Sin actualizar"
                     : "No disponible"
               }
               condition={property.available === undefined ? undefined : property.available}
            />
         </td>
      </tr>
   )
}

function LoadMoreFooter({
   loadedCount,
   totalCount,
   remainingCount,
   isLoading,
   isLoadingAll,
   onLoadMore,
   onLoadAll,
}: {
   loadedCount: number
   totalCount: number
   remainingCount: number
   isLoading: boolean
   isLoadingAll: boolean
   onLoadMore: () => void
   onLoadAll: () => void
}) {
   const nextPageCount = Math.min(PROPERTY_TABLE_PAGE_SIZE, remainingCount)

   return (
      <div className="flex flex-col items-center gap-2 px-4 py-6">
         <button
            type="button"
            className="rounded-md border border-blue px-8 py-3 font-bold text-blue hover:bg-blue hover:text-white disabled:opacity-60"
            disabled={isLoading}
            onClick={onLoadMore}
         >
            {isLoading
               ? "Cargando..."
               : `Mostrar ${formatCount(nextPageCount)} más de ${formatCount(
                    totalCount,
                 )} propiedades`}
         </button>
         <button
            type="button"
            className="rounded-md border border-blue px-8 py-3 font-bold text-blue hover:bg-blue hover:text-white disabled:opacity-60"
            disabled={isLoading}
            onClick={onLoadAll}
         >
            {isLoadingAll
               ? `Cargando ${formatCount(loadedCount)} de ${formatCount(totalCount)}...`
               : "Ver todos los inmuebles"}
         </button>
         <p className="text-sm text-gray-500">
            Mostrando {formatCount(loadedCount)} de {formatCount(totalCount)} propiedades
            disponibles en este condominio
         </p>
      </div>
   )
}

function CondoPropertiesTable({condoId, returnTo}: {condoId: string; returnTo: string}) {
   const {
      properties,
      displayTotal,
      remainingCount,
      hasMore,
      isLoading,
      isLoadingAll,
      hasError,
      sortOption,
      changeSort,
      loadMore,
      loadAll,
      openProperty,
   } = useCondoPropertiesTable({condoId, returnTo})

   return (
      <div className="mb-10">
         <div className="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between">
            <p className="text-sm font-bold text-gray-600">
               {formatCount(displayTotal)} propiedades disponibles
            </p>
            <SortSelect value={sortOption} onChange={changeSort} />
         </div>

         {hasError && (
            <p className="px-4 py-3 text-center text-sm font-bold text-red-600">
               No se pudieron cargar los inmuebles. Intenta de nuevo.
            </p>
         )}

         <div className="overflow-x-auto">
            <table className="min-w-[1480px] w-full border-collapse text-sm">
               <thead>
                  <tr className="bg-gray-100 text-xs font-bold text-gray-700">
                     <th className="border border-gray-200 px-3 py-3 text-left">Inmueble</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">Tipo</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">
                        Precio Venta
                     </th>
                     <th className="border border-gray-200 px-3 py-3 text-center">
                        Precio Renta/noche
                     </th>
                     <th className="border border-gray-200 px-3 py-3 text-center">
                        Precio Renta/año
                     </th>
                     <th className="border border-gray-200 px-3 py-3 text-center">Mantenimiento</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">m²</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">Recámaras</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">Baños</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">
                        Estacionamientos
                     </th>
                     <th className="border border-gray-200 px-3 py-3 text-center">Estatus</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">Visibilidad</th>
                     <th className="border border-gray-200 px-3 py-3 text-center">
                        Disponibilidad
                     </th>
                  </tr>
               </thead>
               <tbody>
                  {properties.map((property, index) => (
                     <PropertyRow
                        key={property.id}
                        property={property}
                        index={index}
                        onOpen={openProperty}
                     />
                  ))}
               </tbody>
            </table>
         </div>

         {isLoading && properties.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-gray-500">Cargando...</p>
         )}

         {hasMore && (
            <LoadMoreFooter
               loadedCount={properties.length}
               totalCount={displayTotal}
               remainingCount={remainingCount}
               isLoading={isLoading}
               isLoadingAll={isLoadingAll}
               onLoadMore={loadMore}
               onLoadAll={loadAll}
            />
         )}
      </div>
   )
}

export default CondoPropertiesTable
